/**
 * 观看历史和观看统计 API 服务
 * 提供与后端 MongoDB watch API 的交互接口
 */

import axios from 'axios';

const API_BASE = '/api/v2/watch';

const watchApi = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器 - 添加认证信息
watchApi.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * 格式化时长（秒 -> 时分秒）
 * @param {number} seconds - 总秒数
 * @returns {string} 格式化后的时长字符串
 */
export const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return '不足1分钟';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    if (minutes > 0) {
      return `${hours}小时${minutes}分钟`;
    }
    return `${hours}小时`;
  }

  if (minutes > 0) {
    return `${minutes}分钟`;
  }

  return `${secs}秒`;
};

/**
 * 格式化日期时间
 * @param {string|Date} date - 日期
 * @returns {string} 格式化后的日期时间
 */
export const formatWatchDate = (date) => {
  if (!date) return '';

  const d = new Date(date);
  const now = new Date();
  const diff = now - d;
  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (minutes < 1) return '刚刚';
  if (minutes < 60) return `${minutes}分钟前`;
  if (hours < 24) return `${hours}小时前`;
  if (days === 1) return '昨天';
  if (days < 7) return `${days}天前`;

  return d.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

/**
 * 获取活动类型描述
 * @param {string} type - 活动类型
 * @returns {string} 描述文本
 */
export const getActivityTypeLabel = (type) => {
  const labels = {
    'watch': '观看',
    'share': '分享',
    'favorite': '收藏',
  };
  return labels[type] || '未知';
};

/**
 * 更新观看进度
 * @param {Object} data - 进度数据
 * @param {string} data.user_id - 用户ID
 * @param {string} data.drama_id - 剧集ID
 * @param {string} data.episode_id - 集数ID
 * @param {number} data.progress - 观看进度（0-100）
 * @param {number} data.duration - 观看时长（秒，可选）
 * @returns {Promise} API 响应
 */
export const updateProgress = async (data) => {
  try {
    const response = await watchApi.post('/progress', data);
    return response.data;
  } catch (error) {
    console.error('更新观看进度失败:', error);
    return {
      success: false,
      message: error.response?.data?.message || '更新失败',
    };
  }
};

/**
 * 记录分享行为
 * @param {Object} data - 分享数据
 * @param {string} data.user_id - 用户ID
 * @param {string} data.drama_id - 剧集ID
 * @param {string} data.episode_id - 集数ID（可选）
 * @param {string} data.share_platform - 分享平台（可选）
 * @returns {Promise} API 响应
 */
export const recordShare = async (data) => {
  try {
    const response = await watchApi.post('/share', data);
    return response.data;
  } catch (error) {
    console.error('记录分享失败:', error);
    return {
      success: false,
      message: error.response?.data?.message || '记录失败',
    };
  }
};

/**
 * 记录收藏行为
 * @param {Object} data - 收藏数据
 * @param {string} data.user_id - 用户ID
 * @param {string} data.drama_id - 剧集ID
 * @returns {Promise} API 响应
 */
export const recordFavorite = async (data) => {
  try {
    const response = await watchApi.post('/favorite', data);
    return response.data;
  } catch (error) {
    console.error('记录收藏失败:', error);
    return {
      success: false,
      message: error.response?.data?.message || '记录失败',
    };
  }
};

/**
 * 获取观看历史（简单版）
 * @param {string} userId - 用户ID
 * @param {number} limit - 返回数量限制
 * @returns {Promise} API 响应
 */
export const getWatchHistory = async (userId, limit = 50) => {
  try {
    const response = await watchApi.get(`/history/${userId}`, {
      params: { limit },
    });
    return response.data;
  } catch (error) {
    console.error('获取观看历史失败:', error);
    return {
      success: false,
      message: error.response?.data?.message || '获取失败',
    };
  }
};

/**
 * 获取带剧集详情的观看历史（分页版）
 * @param {string} userId - 用户ID
 * @param {number} page - 页码
 * @param {number} pageSize - 每页数量
 * @returns {Promise} API 响应
 */
export const getWatchHistoryDetails = async (userId, page = 1, pageSize = 20) => {
  try {
    const response = await watchApi.get(`/history-details/${userId}`, {
      params: { page, page_size: pageSize },
    });
    return response.data;
  } catch (error) {
    console.error('获取观看历史详情失败:', error);
    return {
      success: false,
      message: error.response?.data?.message || '获取失败',
    };
  }
};

/**
 * 获取观看统计（简单版）
 * @param {string} userId - 用户ID
 * @returns {Promise} API 响应
 */
export const getWatchStats = async (userId) => {
  try {
    const response = await watchApi.get(`/stats/${userId}`);
    return response.data;
  } catch (error) {
    console.error('获取观看统计失败:', error);
    return {
      success: false,
      message: error.response?.data?.message || '获取失败',
    };
  }
};

/**
 * 获取观看概览统计（4个模块完整版）
 * @param {string} userId - 用户ID
 * @returns {Promise} API 响应
 */
export const getWatchOverview = async (userId) => {
  try {
    const response = await watchApi.get(`/overview/${userId}`);
    return response.data;
  } catch (error) {
    console.error('获取观看概览失败:', error);
    return {
      success: false,
      message: error.response?.data?.message || '获取失败',
    };
  }
};

/**
 * 检查用户是否已观看某集
 * @param {string} userId - 用户ID
 * @param {string} dramaId - 剧集ID
 * @param {string} episodeId - 集数ID
 * @returns {Promise} API 响应
 */
export const checkWatchStatus = async (userId, dramaId, episodeId) => {
  try {
    const response = await watchApi.get('/check', {
      params: {
        user_id: userId,
        drama_id: dramaId,
        episode_id: episodeId,
      },
    });
    return response.data;
  } catch (error) {
    console.error('检查观看状态失败:', error);
    return {
      success: false,
      message: error.response?.data?.message || '检查失败',
    };
  }
};

/**
 * 获取当前用户的 user_id
 * 从 localStorage 中读取 Mongo 用户信息
 * @returns {string|null} 用户ID 或 null（未登录时）
 */
export const getCurrentUserId = () => {
  try {
    const userStr = localStorage.getItem('mongo_user');
    if (userStr) {
      const user = JSON.parse(userStr);
      return user.user_id || user._id || user.id || user.userId;
    }
  } catch (e) {
    console.error('解析用户信息失败:', e);
  }
  return null;
};

/**
 * 获取 guest 用户 ID（未登录时使用）
 * @returns {string} guest 用户标识
 */
export const getGuestUserId = () => {
  // 实际的 guest 用户 ID 由后端根据邮箱查询
  // 这里传递 'guest' 标识，后端会自动转换
  return 'guest';
};

// ============================================================
// 兼容封装 - 供 DramaDetail.js 等老代码使用
// ============================================================

/**
 * 分享剧集（封装 recordShare）
 * @param {string} userId - 用户ID
 * @param {string} dramaId - 剧集ID
 * @param {string} episodeId - 集数ID
 * @param {string} platform - 分享平台
 */
const shareDrama = async (userId, dramaId, episodeId = 'all', platform = '') => {
  return await recordShare({
    user_id: userId,
    drama_id: dramaId,
    episode_id: episodeId,
    share_platform: platform,
  });
};

/**
 * 收藏剧集（封装 recordFavorite）
 * @param {string} userId - 用户ID
 * @param {string} dramaId - 剧集ID
 */
const favoriteDrama = async (userId, dramaId) => {
  return await recordFavorite({
    user_id: userId,
    drama_id: dramaId,
  });
};

// 挂到默认导出对象上，DramaDetail.js 通过 watchApi.shareDrama() 调用
watchApi.shareDrama = shareDrama;
watchApi.favoriteDrama = favoriteDrama;
export { shareDrama, favoriteDrama };

// ============================================================
// WatchProgressTracker - 视频播放进度跟踪器
// 供 DramaPlayer.js 使用，按时间间隔节流上报观看进度
// ============================================================

/**
 * 观看进度跟踪器
 * 用法：
 *   const tracker = new WatchProgressTracker(userId, dramaId, episodeId, 30);
 *   tracker.update(currentTime, duration);  // 节流上报
 *   tracker.onPlayEnd(100);                  // 播放结束强制上报
 *   tracker.destroy();                        // 切换/卸载时清理
 */
export class WatchProgressTracker {
  constructor(userId, dramaId, episodeId, intervalSeconds = 30) {
    this.userId = userId;
    this.dramaId = dramaId;
    this.episodeId = episodeId || 'all';
    this.intervalMs = Math.max(5, intervalSeconds) * 1000;
    this.lastReportAt = 0;
    this.lastProgress = -1;
    this.destroyed = false;
    this.completedReported = false;
  }

  /**
   * 视频 timeupdate 时调用，按节流时间窗口上报进度
   * @param {number} currentTime - 当前播放秒数
   * @param {number} duration - 视频总时长秒数
   */
  async update(currentTime, duration) {
    if (this.destroyed) return null;
    if (!this.userId || !this.dramaId || !this.episodeId) return null;
    if (!duration || duration <= 0) return null;

    const now = Date.now();
    const progress = Math.min(100, Math.max(0, Math.round((currentTime / duration) * 100)));

    // 首次上报立即触发；后续按节流：距上次上报不到 intervalMs 且进度变化 < 5% 则跳过
    if (
      this.lastReportAt > 0 &&
      now - this.lastReportAt < this.intervalMs &&
      Math.abs(progress - this.lastProgress) < 5
    ) {
      return null;
    }

    this.lastReportAt = now;
    this.lastProgress = progress;

    return await this._report(progress, Math.floor(currentTime));
  }

  /**
   * 视频播放结束时调用，强制上报 100% 进度
   */
  async onPlayEnd(progress = 100) {
    if (this.destroyed || this.completedReported) return null;
    if (!this.userId || !this.dramaId || !this.episodeId) return null;
    this.completedReported = true;
    this.lastReportAt = Date.now();
    this.lastProgress = progress;
    return await this._report(progress, 0);
  }

  /**
   * 内部上报方法
   */
  async _report(progress, duration) {
    try {
      const result = await updateProgress({
        user_id: this.userId,
        drama_id: this.dramaId,
        episode_id: this.episodeId,
        progress,
        duration,
      });
      return result;
    } catch (e) {
      console.error('[WatchProgressTracker] 上报失败:', e);
      return { success: false, message: e.message };
    }
  }

  /**
   * 销毁跟踪器（切集/卸载时调用）
   */
  destroy() {
    this.destroyed = true;
  }
}

export default watchApi;
